import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SiteLayout, useDocumentTitle } from '../../components/platform/SiteLayout';
import { Container, Heading, Card, Btn, Field, inputCls } from '../../components/platform/ui';
import useToolTask from '../../components/tools/useToolTask';
import { FilePicker, TaskProgress, ResultImage, DownloadButton, Slider, buildForm } from '../../components/tools/ToolShared';

const Triple = ({ label, v, unit }) => (
  <div className="text-sm"><span className="font-bold">{label}: </span>{['x', 'y', 'z'].map((a) => `${a} ${v?.[a]}${unit}`).join(', ')}</div>
);

const SuperimposeToolPage = () => {
  const { t } = useTranslation();
  useDocumentTitle(t('pl.toolsPage.overlay.title'));
  const task = useToolTask('tools.task.superimpose');
  const [tab, setTab] = useState('superimpose');
  const [before, setBefore] = useState([]);
  const [after, setAfter] = useState([]);
  const [scan, setScan] = useState([]);
  const [roi, setRoi] = useState('cranial');
  const [fraction, setFraction] = useState(0.45);
  const [rot, setRot] = useState({ x: 0, y: 0, z: 0 });
  const [spacing, setSpacing] = useState('');

  // Если открыли страницу с сохранённой задачей другого режима, показываем вкладку этого режима
  useEffect(() => {
    const k = task.results?.kind;
    if ((k === 'superimpose' || k === 'orient') && k !== tab) setTab(k);
  }, [task.results]);

  const busy = task.phase === 'uploading' || task.phase === 'processing';
  const changeTab = (next) => { if (!busy) { task.reset(); setTab(next); } };
  const runSuperimpose = () => task.start('superimpose', buildForm({ before, after }, { roi, roi_fraction: fraction }));
  const runOrient = () => task.start('orient', buildForm({ files: scan }, { rotate_x: rot.x, rotate_y: rot.y, rotate_z: rot.z, spacing_mm: spacing }));
  const rerunOrient = () => task.rerun({ rotate_x: rot.x, rotate_y: rot.y, rotate_z: rot.z, spacing_mm: spacing });
  const report = task.results?.report;
  const o = 'pl.toolsPage.overlay';
  const doneKind = task.phase === 'done' ? task.results?.kind : null;

  return (
    <SiteLayout>
      <Container className="py-10 max-w-4xl">
        <Heading level={1} className="text-3xl mb-2">{t(`${o}.title`)}</Heading>
        <p className="mb-6 text-gray-700">{t(`${o}.subtitle`)}</p>

        <div className="flex gap-2 mb-6" role="tablist">
          {[['superimpose', 'tabSuperimpose'], ['orient', 'tabOrient']].map(([id, key]) => (
            <button key={id} role="tab" aria-selected={tab === id} onClick={() => changeTab(id)}
                    className={`engraved-button ${tab === id ? 'engraved-button-red' : ''}`}>{t(`${o}.${key}`)}</button>
          ))}
        </div>

        <TaskProgress task={task} />

        {tab === 'superimpose' ? (
          <Card className="mb-6">
            <FilePicker label={t(`${o}.before`)} files={before} onChange={setBefore} disabled={busy} />
            <FilePicker label={t(`${o}.after`)} files={after} onChange={setAfter} disabled={busy} />
            <Field label={t(`${o}.roi`)} hint={t(`${o}.roiHint`)}>
              <select className={inputCls} value={roi} disabled={busy} onChange={(e) => setRoi(e.target.value)}>
                <option value="cranial">{t(`${o}.roiCranial`)}</option>
                <option value="full">{t(`${o}.roiFull`)}</option>
              </select>
            </Field>
            {roi === 'cranial' && (
              <Slider label={t(`${o}.roiFraction`)} value={fraction} min={0.3} max={0.7} step={0.05} onChange={setFraction} disabled={busy} />
            )}
            <Btn className="mt-3" disabled={busy || !before.length || !after.length} onClick={runSuperimpose}>{t(`${o}.run`)}</Btn>
          </Card>
        ) : (
          <Card className="mb-6">
            <FilePicker label={t(`${o}.scan`)} files={scan} onChange={setScan} disabled={busy} />
            <div className="text-sm font-bold">{t(`${o}.rotation`)}</div>
            <div className="grid sm:grid-cols-3 gap-x-4">
              {['x', 'y', 'z'].map((axis) => (
                <Slider key={axis} label={t(`pl.toolsPage.ceph.rotate${axis.toUpperCase()}`)} value={rot[axis]} min={-45} max={45} step={0.5}
                        unit="°" disabled={busy} onChange={(v) => setRot((r) => ({ ...r, [axis]: v }))} />
              ))}
            </div>
            <Field label={t(`${o}.spacing`)}>
              <input type="number" min="0" step="0.1" className={inputCls} value={spacing} disabled={busy} onChange={(e) => setSpacing(e.target.value)} />
            </Field>
            <Btn className="mt-3" disabled={busy || !scan.length} onClick={runOrient}>{t(`${o}.orientRun`)}</Btn>
          </Card>
        )}

        {doneKind === 'superimpose' && tab === 'superimpose' && (
          <Card className="mb-6">
            <Heading level={3} className="text-lg mb-3">{t(`${o}.report`)}</Heading>
            {report && (
              <div className="space-y-1 mb-4">
                <Triple label={t(`${o}.rotation`)} v={report.rotation_deg} unit="°" />
                <Triple label={t(`${o}.translation`)} v={report.translation_mm} unit=" мм" />
                <div className="text-sm"><span className="font-bold">{t(`${o}.correlation`)}: </span>{report.correlation_before} → {report.correlation_after}</div>
                {report.improved === false && <p className="text-sm text-red-800 font-bold mt-2">{t(`${o}.notImproved`)}</p>}
              </div>
            )}
            <p className="text-xs text-gray-600 mb-3">{t(`${o}.legend`)}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[['overlay_before_sagittal.png', 'overlayBefore'], ['overlay_sagittal.png', 'overlaySagittal'],
                ['overlay_coronal.png', 'overlayCoronal'], ['overlay_axial.png', 'overlayAxial']].map(([name, key]) => (
                <figure key={name}>
                  <ResultImage taskId={task.taskId} name={name} alt={t(`${o}.${key}`)} className="w-full border-2 border-black bg-black" />
                  <figcaption className="text-xs mt-1">{t(`${o}.${key}`)}</figcaption>
                </figure>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-4">
              <DownloadButton taskId={task.taskId} name="after_registered.nii.gz" label={t(`${o}.fileRegistered`)} variant="primary" />
              <DownloadButton taskId={task.taskId} name="transform.tfm" label={t(`${o}.fileTransform`)} />
            </div>
            <p className="text-xs text-gray-500 mt-3">{t(`${o}.verify`)}</p>
            <p className="text-xs text-gray-500 mt-1">{t('pl.toolsPage.common.retention')}</p>
          </Card>
        )}

        {doneKind === 'orient' && tab === 'orient' && (
          <Card className="mb-6">
            <Heading level={3} className="text-lg mb-3">{t(`${o}.previews`)}</Heading>
            <div className="grid sm:grid-cols-3 gap-4">
              {['axial', 'coronal', 'sagittal'].map((p) => (
                <ResultImage key={p} taskId={task.taskId} name={`preview_${p}.png`} alt={p} className="w-full border-2 border-black bg-black" />
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-4">
              <DownloadButton taskId={task.taskId} name="oriented.nii.gz" label={t(`${o}.fileOriented`)} variant="primary" />
              <Btn variant="secondary" onClick={rerunOrient}>{t('pl.toolsPage.common.recalc')}</Btn>
            </div>
            <p className="text-xs text-gray-500 mt-2">{t('pl.toolsPage.common.retention')}</p>
          </Card>
        )}
        <p className="text-sm text-gray-600">{t('pl.toolsPage.common.disclaimer')}</p>
      </Container>
    </SiteLayout>
  );
};

export default SuperimposeToolPage;
